import { useState } from 'react';
import { type ScheduleSettings, type HolidayInfo, formatDateFull } from '@/lib/scheduleUtils';

interface Props {
  show: boolean;
  settings: ScheduleSettings | null;
  settingsForm: {
    day1: boolean; day2: boolean; day3: boolean; day4: boolean; day5: boolean; day6: boolean; day0: boolean;
    exclude_public_holidays: boolean;
    holiday_country: string;
    holiday_region: string;
  };
  projectHolidays: HolidayInfo[];
  projectId: string;
  saving: boolean;
  savingProjHoliday: boolean;
  saveError: string;
  onClose: () => void;
  onSaveSettings: () => Promise<void>;
  onSettingsFormChange: (form: Props['settingsForm']) => void;
  onLoadHolidays: () => Promise<void>;
  onAddProjectHoliday?: (date: string, name: string) => Promise<void>;
  onRemoveProjectHoliday?: (date: string) => Promise<void>;
}

export default function ScheduleSettingsModal({
  show,
  settings,
  settingsForm,
  projectHolidays,
  projectId,
  saving,
  savingProjHoliday,
  saveError,
  onClose,
  onSaveSettings,
  onSettingsFormChange,
  onLoadHolidays,
  onAddProjectHoliday,
  onRemoveProjectHoliday,
}: Props) {
  const [projHolidayForm, setProjHolidayForm] = useState({ holiday_date: '', holiday_name: '' });

  if (!show) return null;

  const handleDayChange = (key: keyof typeof settingsForm, value: boolean) => {
    onSettingsFormChange({ ...settingsForm, [key]: value });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-sm rounded-3xl overflow-hidden mx-4 max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h3 className="text-[#1A1A1A] font-bold text-base">Schedule Settings</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">Working days</p>
          <div className="grid grid-cols-2 gap-2">
            {([
              { key: 'day1' as const, label: 'Monday' },
              { key: 'day2' as const, label: 'Tuesday' },
              { key: 'day3' as const, label: 'Wednesday' },
              { key: 'day4' as const, label: 'Thursday' },
              { key: 'day5' as const, label: 'Friday' },
              { key: 'day6' as const, label: 'Saturday' },
              { key: 'day0' as const, label: 'Sunday' },
            ] as const).map((d) => (
              <label key={d.key} className="flex items-center gap-2.5 bg-[#F9FAFB] rounded-xl px-3 py-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settingsForm[d.key]}
                  onChange={(e) => handleDayChange(d.key, e.target.checked)}
                  className="w-4 h-4 accent-[#E8621A]"
                />
                <span className="text-sm text-[#374151] font-medium">{d.label}</span>
              </label>
            ))}
          </div>
          <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider pt-1">Holidays</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Holiday Country</label>
              <input
                type="text"
                value={settingsForm.holiday_country}
                onChange={(e) => handleDayChange('holiday_country' as any, e.target.value.toUpperCase())}
                placeholder="e.g. NZ"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A]"
              />
            </div>
            <div>
              <label className="block text-[#555555] text-xs font-semibold mb-1.5">Region (optional)</label>
              <input
                type="text"
                value={settingsForm.holiday_region}
                onChange={(e) => handleDayChange('holiday_region' as any, e.target.value)}
                placeholder="e.g. Auckland"
                className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A]"
              />
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settingsForm.exclude_public_holidays}
              onChange={(e) => handleDayChange('exclude_public_holidays' as any, e.target.checked)}
              className="w-4 h-4 accent-[#E8621A]"
            />
            <span className="text-sm text-[#374151]">Exclude public holidays from working days</span>
          </label>

          <div className="pt-2 space-y-3">
            <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider">Project-specific holidays</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">Date</label>
                <input
                  type="date"
                  value={projHolidayForm.holiday_date}
                  onChange={(e) => setProjHolidayForm((p) => ({ ...p, holiday_date: e.target.value }))}
                  className="w-full min-w-0 bg-white border border-[#E0DDD8] rounded-xl px-3 py-3 text-sm text-[#1A1A1A] block"
                />
              </div>
              <div>
                <label className="block text-[#555555] text-xs font-semibold mb-1.5">Holiday Name</label>
                <input
                  type="text"
                  value={projHolidayForm.holiday_name}
                  onChange={(e) => setProjHolidayForm((p) => ({ ...p, holiday_name: e.target.value }))}
                  placeholder="e.g. Custom closure"
                  className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCCCCC]"
                />
              </div>
            </div>
            <button
              onClick={async () => {
                if (!projHolidayForm.holiday_date || !projHolidayForm.holiday_name.trim()) return;
                if (onAddProjectHoliday) {
                  await onAddProjectHoliday(projHolidayForm.holiday_date, projHolidayForm.holiday_name.trim());
                  setProjHolidayForm({ holiday_date: '', holiday_name: '' });
                }
              }}
              disabled={!projHolidayForm.holiday_date || !projHolidayForm.holiday_name.trim() || savingProjHoliday}
              className="w-full py-2.5 rounded-xl bg-[#1A1A1A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingProjHoliday ? <><i className="ri-loader-4-line animate-spin"></i>Adding...</> : 'Add Project Holiday'}
            </button>

            {projectHolidays.length > 0 && (
              <div className="space-y-1.5">
                {projectHolidays.map((h) => (
                  <div key={h.date} className="flex items-center justify-between bg-[#F9FAFB] rounded-xl px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#374151]">{h.name || 'Holiday'}</p>
                      <p className="text-[10px] text-[#9CA3AF]">{formatDateFull(h.date)}</p>
                    </div>
                    <button
                      onClick={async () => {
                        if (onRemoveProjectHoliday) {
                          await onRemoveProjectHoliday(h.date);
                        }
                      }}
                      className="text-[#EF4444] text-[10px] font-bold cursor-pointer whitespace-nowrap ml-2"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {saveError && <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{saveError}</p>}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onSaveSettings}
            disabled={saving}
            className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <><i className="ri-loader-4-line animate-spin"></i>Saving...</> : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}